import { createSlice } from '@reduxjs/toolkit';
import { membersApi } from '@store/rtk/members';
import { TableData } from '@uikit/Tables/types';
import { beautifyDateTime } from '@src/utils';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';

type BenefitsSliceType = {
  limitationsData: TableData;
  serviceAuthorizationsData: TableData;
  eligibilityTransactionsData: TableData;
  isCheckAuthorizationNumberBool: boolean;
};

type serviceAuthorizationTableDataType = {
  id: number;
  effectiveDate: string;
  expirationDate: string;
  healthPlan: string;
  modeOfTransport: string;
  authorizationNumber: string;
  status: boolean;
};

type getServiceAuthorizationsPayloadType = {
  content: Array<serviceAuthorizationTableDataType>;
  last: boolean;
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  filter: any;
  table: string | null | undefined;
};

const {
  limitationsColumns,
  serviceAuthorizationsColumns,
  eligibilityTransactionsColumns,
} = TABLE_COLUMNS;

const initialState: BenefitsSliceType = {
  limitationsData: {
    columns: limitationsColumns,
    rows: [],
    size: 5,
  },
  serviceAuthorizationsData: {
    columns: serviceAuthorizationsColumns,
    rows: [],
    size: 5,
  },
  eligibilityTransactionsData: {
    columns: eligibilityTransactionsColumns,
    rows: [],
    size: 5,
  },
  isCheckAuthorizationNumberBool: false,
};

export const benefitsSlice = createSlice({
  name: 'benefits',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(
        membersApi.endpoints.getLimitations.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.limitationsData = {
              ...state.limitationsData,
              rows: [
                ...payload?.content?.map((el, idx) => ({
                  ...el,
                  effective: beautifyDateTime(el?.effectiveDate),
                  expiration: beautifyDateTime(el?.expirationDate),
                  lineOfBusiness: el?.lob ?? '',
                })),
              ],
              ...payload,
              requestTablePageChange: membersApi.endpoints.getLimitations,
            };
          }
        }
      )
      .addMatcher(
        membersApi.endpoints.getServiceAuthorizations.matchFulfilled,
        (
          state,
          { payload }: { payload: getServiceAuthorizationsPayloadType }
        ) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.serviceAuthorizationsData = {
              ...state.serviceAuthorizationsData,
              rows: [
                ...payload.content.map((el, idx) => ({
                  ...el,
                  effectiveDate: beautifyDateTime(el?.effectiveDate),
                  expirationDate: beautifyDateTime(el?.expirationDate),
                  status: el.status ? 'Active' : 'InActive',
                })),
              ],
              ...payload,
              requestTablePageChange:
                membersApi.endpoints.getServiceAuthorizations,
              filter: payload.filter || {},
            };
          }
        }
      )
      .addMatcher(
        membersApi.endpoints.getEligibilityTransactions.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.eligibilityTransactionsData = {
              ...state.eligibilityTransactionsData,
              rows: [
                ...payload.content.map((el, idx) => ({
                  ...el,
                  effectiveDate: beautifyDateTime(el?.effectiveDate),
                  expirationDate: beautifyDateTime(el?.expirationDate),
                  importedDate: beautifyDateTime(el?.importedDate),
                })),
              ],
              ...payload,
              requestTablePageChange:
                membersApi.endpoints.getEligibilityTransactions,
            };
          }
        }
      )
      .addMatcher(
        membersApi.endpoints.isCheckAuthorizationNumber.matchFulfilled,
        (state, { payload }) => {
          state.isCheckAuthorizationNumberBool = payload.exists;
        }
      );
  },
});

export const selectBenefits = (state: { benefits: BenefitsSliceType }) =>
  state.benefits;
export const benefitsReducer = benefitsSlice.reducer;
